// BTP-Suivi · SousTraitantLayout — Layout partagé espace sous-traitant.
// Header sticky + sous-ligne + bottom nav 5 tabs + <Outlet />.
// Spec : docs/SPEC_SOUSTRAITANT_MODULE.md §5.4.1 §5.4.2
// Prototype : docs/soustraitant-prototype.jsx (App, BottomTab)
import React from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Menu,
  Search,
  Bell,
  Activity,
  Building2,
  Wrench,
  Euro,
  User,
} from "lucide-react";
import { soustraitantApi } from "../../lib/soustraitantApi";

// ─── Bottom tab item ──────────────────────────────────────────────────────────

function BottomTab({ label, icon, active, onClick, count, alert }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-current={active ? "page" : undefined}
      className={`flex-1 py-2 flex flex-col items-center gap-0.5 relative transition-colors ${
        active ? "text-foreground" : "text-muted-foreground"
      }`}
      style={{ minHeight: 44 }}
    >
      <div className="relative">
        {icon}
        {count > 0 && (
          <span
            aria-label={`${count} en attente`}
            className={`absolute -top-1 -right-2 text-white text-[8px] font-bold w-3.5 h-3.5 rounded-full flex items-center justify-center ${
              alert ? "bg-rose-500" : "bg-indigo-500"
            }`}
          >
            {count}
          </span>
        )}
      </div>
      <span className="text-[9px] font-medium">{label}</span>
    </button>
  );
}

// ─── Layout ───────────────────────────────────────────────────────────────────

export default function SousTraitantLayout() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { data: me } = useQuery({
    queryKey: ["st-me"],
    queryFn: soustraitantApi.getMe,
  });

  const { data: tachesAFaire = [] } = useQuery({
    queryKey: ["st-taches-todo"],
    queryFn: () => soustraitantApi.listTaches({ statut: "a_faire" }),
    enabled: !!me,
  });

  const { data: factures = [] } = useQuery({
    queryKey: ["st-factures"],
    queryFn: () => soustraitantApi.listFactures(),
    enabled: !!me,
  });

  const facturesEnAttente = factures.filter((f) => f.statut === "envoyee");

  // Initiales de l'utilisateur pour l'avatar
  const initials = me?.nom_contact
    ?.split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "?";

  const isEntreprise = me?.type === "entreprise";

  // Déterminer l'onglet actif
  const tab = pathname.startsWith("/sous-traitant/chantiers")
    ? "chantiers"
    : pathname.startsWith("/sous-traitant/taches")
    ? "taches"
    : pathname.startsWith("/sous-traitant/finances")
    ? "finances"
    : pathname.startsWith("/sous-traitant/profil")
    ? "profil"
    : "accueil";

  // Break out of AppLayout padding (same technique as SousTraitantHome).
  // AppLayout adds max-w-7xl + p-4 md:p-6 — we negate it to take full width.
  return (
    <div className="-m-4 md:-m-6 min-h-screen bg-muted font-sans pb-20">
      {/* ── Header ── */}
      <header className="bg-card border-b border-border sticky top-0 z-20">
        <div className="px-4 py-3 flex items-center justify-between gap-3">
          <button
            type="button"
            aria-label="Retour à l'accueil"
            onClick={() => navigate("/sous-traitant")}
            className="text-foreground p-1 hover:bg-muted rounded-lg transition-colors"
            style={{ minWidth: 44, minHeight: 44 }}
          >
            <Menu size={22} aria-hidden="true" />
          </button>

          <div className="flex items-center gap-2">
            <button
              type="button"
              aria-label="Rechercher"
              onClick={() => toast("Recherche bientôt disponible")}
              className="text-muted-foreground p-2 hover:bg-muted rounded-lg transition-colors"
              style={{ minWidth: 44, minHeight: 44 }}
            >
              <Search size={20} aria-hidden="true" />
            </button>
            <button
              type="button"
              aria-label="Mes factures en attente"
              onClick={() => navigate("/sous-traitant/finances")}
              className="relative text-muted-foreground p-2 hover:bg-muted rounded-lg transition-colors"
              style={{ minWidth: 44, minHeight: 44 }}
            >
              <Bell size={20} aria-hidden="true" />
              {facturesEnAttente.length > 0 && (
                <span className="absolute top-1 right-1 bg-rose-500 text-white text-[9px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                  {facturesEnAttente.length}
                </span>
              )}
            </button>
            <button
              type="button"
              aria-label="Mon profil"
              onClick={() => navigate("/sous-traitant/profil")}
              className="bg-primary text-primary-foreground w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold hover:bg-primary/90 transition-colors"
            >
              {initials}
            </button>
          </div>
        </div>

        {/* Sous-ligne : label espace + raison sociale + badge type */}
        <div className="px-4 pb-2.5 flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-widest text-muted-foreground">
              ESPACE SOUS-TRAITANT
            </div>
            <div className="text-sm font-semibold text-foreground">
              {me?.raison_sociale ?? "Chargement…"}
            </div>
          </div>
          {me && (
            <div className="bg-indigo-50 text-foreground text-[10px] px-2 py-1 rounded-full font-bold">
              {isEntreprise ? "🏢 ENTREPRISE" : "👤 INDÉPENDANT"}
            </div>
          )}
        </div>
      </header>

      {/* ── Contenu (Outlet) ── */}
      <main className="px-4 py-3 space-y-3 max-w-3xl mx-auto" id="st-main">
        <Outlet />
      </main>

      {/* ── Bottom Nav ── */}
      <nav
        className="fixed bottom-0 left-0 right-0 bg-card border-t border-border flex z-20"
        aria-label="Navigation principale"
      >
        <BottomTab
          label="Accueil"
          icon={<Activity size={20} aria-hidden="true" />}
          active={tab === "accueil"}
          onClick={() => navigate("/sous-traitant")}
        />
        <BottomTab
          label="Chantiers"
          icon={<Building2 size={20} aria-hidden="true" />}
          active={tab === "chantiers"}
          onClick={() => navigate("/sous-traitant/chantiers")}
        />
        <BottomTab
          label="Tâches"
          icon={<Wrench size={20} aria-hidden="true" />}
          active={tab === "taches"}
          onClick={() => navigate("/sous-traitant/taches")}
          count={tachesAFaire.length}
        />
        <BottomTab
          label="Finances"
          icon={<Euro size={20} aria-hidden="true" />}
          active={tab === "finances"}
          onClick={() => navigate("/sous-traitant/finances")}
          count={facturesEnAttente.length}
          alert
        />
        <BottomTab
          label="Profil"
          icon={<User size={20} aria-hidden="true" />}
          active={tab === "profil"}
          onClick={() => navigate("/sous-traitant/profil")}
        />
      </nav>
    </div>
  );
}
